import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactLeafletGoogleLayer from "react-leaflet-google-layer";
import {
  MapContainer,
  Marker,
  FeatureGroup,
  Polygon,
  Tooltip,
  Polyline,
  TileLayer,
} from "react-leaflet";
import L from "leaflet";
import { EditControl } from "react-leaflet-draw";
import asset from "json/assets";
import OptionList from "components/OptionList/OptionList";
import styles from "styles/pages/Map1.module.scss";
import stylesRightList from "styles/component/RightList.module.scss";
import eventEmitter from "services/event.emitter";
import events from "json/events";
import { booleanPointInPolygon } from "@turf/turf";
import {
  AzureMapAPIKey,
  BingMapAPIKey,
  GoogleMapAPIKey,
  defaultZoneColor,
  drawEventType,
  drawShapeType,
  elementGroup,
  singlePointLayers,
  singleShapeElements,
  zonePrefixList,
} from "json/constants";
import {
  catagorizeShapeData,
  convertAddedShapeToPayload,
  convertPolygonDataToString,
  convertSingleLatLngStringtoFloatArray,
  convertToAPIPayload,
  convertToLabelName,
  getBoundsAndElementWisePoints,
  getSelectedZoneFromEditableArea,
  isColor,
  preparePointsToLatLong,
} from "services/utils";
import {
  useAddedShapedStatus,
  useEditableArea,
  useMapShapeData,
  useSelectedGroup,
  useSelectedZone,
  useSelectedZoneType,
  useShapePropertiesColor,
  useShapePropertiesType,
  useVisibleAreas,
  useVisibleZoneList,
} from "hooks/MapDetails";
import { useDispatch } from "react-redux";
import {
  removeAllMapData,
  resetShapeProperties,
  setAddedShapePoints,
  setAddedShapeStatus,
  setEditableArea,
  setMapData,
} from "redux/MapSlice";
import { useMapControls } from "hooks/useMapBasicControls";
import { useEventEmitter } from "hooks/useEventEmitter";
import { useEscKeyPress } from "hooks/useEscKeyPress";
import { useRefHook } from "hooks/useRefHook";
import CustomPopover from "components/CustomPopover/CustomPopover";
import "leaflet/dist/leaflet.css";
import BasicControlOption from "components/BasicControlOption/BasicControlOption";
import { BingLayer } from "react-leaflet-bing-v2";
import useNotiStack from "hooks/useNotistack";

delete L.Icon.Default.prototype._getIconUrl; //deleting default icon given by react leaflet draw.

const GoogleLeaflet = () => {
  React.useEffect(() => {
    //setting default marker
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
      iconUrl: require("leaflet/dist/images/marker-icon.png"),
      shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
    });
  }, []);

  React.useEffect(() => {
    //-----leaflet uses polyline to create polygons; other shapes do not have a error object! (._.')

    //error tooltip..............
    L.drawLocal.draw.handlers.polyline.error =
      "<strong>Oops!</strong> You cannot draw a shape like that!";

    //custom tooltip messages --------------   console.log(' L.drawLocal', L.drawLocal);
    L.drawLocal.draw.handlers.polygon.tooltip = {
      start: "Click to start drawing shape<br/> or press Esc key to cancel",
      cont: "Click to continue drawing shape<br/> or press Esc key to cancel",
      end: "Click first point to close this shape<br/> or press Esc key to cancel",
    };
    L.drawLocal.draw.handlers.polyline.tooltip = {
      start: "Click to start drawing line <br/> or press Esc key to cancel",
      cont: "Click to continue drawing line<br/> or press Esc key to cancel",
      end: "Click last point to finish line<br/> or press Esc key to cancel",
    };

    L.drawLocal.draw.handlers.marker.tooltip = {
      start: "Click map to place marker.<br/> Or press Esc key to cancel",
    };

    L.drawLocal.edit.handlers.edit.tooltip.subtext =
      " Press Esc key to undo changes.";
    L.drawLocal.edit.handlers.edit.tooltip.text =
      " Drag vertices or markers to edit shapes.";
  }, []);
  const dispatch = useDispatch();
  const { toastError } = useNotiStack();
  const selectedZone = useSelectedZone();
  const selectedZoneType = useSelectedZoneType();
  const selectedZoneTypeRef = useRefHook(selectedZoneType);
  // const selectedZoneDetails = useSelectedZoneDetails();
  const shapeData = useMapShapeData();
  const selectedGroup = useSelectedGroup();
  const selectedGroupRef = useRefHook(selectedGroup);

  const visibleAreasList = useVisibleAreas();
  const editableArea = useEditableArea();
  const selectedZoneDetails =
    shapeData[`${getSelectedZoneFromEditableArea(editableArea)}`];
  const selectedZoneDetailsRef = useRefHook(selectedZoneDetails);

  const editableAreaRef = useRefHook(editableArea);
  const invisibleZoneList = useVisibleZoneList();
  const addedShapeStatus = useAddedShapedStatus();
  const shapePropColor = useShapePropertiesColor();
  const shapePropType = useShapePropertiesType();
  // const ZOOM = 17;
  const MAX_ZOOM = 22;
  const MIN_ZOOM = 15;
  const SCALE_DASH = [10, 5];
  // const currentLocation = [47.252007772358, -101.7813295126];
  const mapRef = useRef();
  const editControlRef = useRef();
  const featureGroupRef = useRef(null);
  const [isGoogleView, setIsGoogleView] = useState(true);

  const {
    zoomIn,
    zoomOut,
    drawPolyLine,
    drawPolygon,
    drawPoint,
    disableDraw,
    // flyTo,
    // setMapView,
  } = useMapControls(mapRef, editControlRef, isGoogleView);

  let drawControl = editControlRef.current;

  const [currentLocation, setCurrentLocation] = useState(null);
  const [mapControlList, setMapControlList] = useState([]);
  const [allPolyData, setAllPolyData] = useState([]);

  // const [mapPolygonData, setMapPolygonData] = useState([]);
  const [editPolygonData, setEditPolygonData] = useState(null);
  const [editMarkerData, setEditMarkerData] = useState(null);

  const [editComplete, setEditComplete] = useState();
  const [addedLayer, setAddedLayer] = useState([]);
  // const [scaleLayer, setScaleLayer] = useState([]); // to get the current scale layer : contains the whole layer
  const [scaleLineData, setScaleLineData] = useState([]);
  const [addedPoints, setAddedPoints] = useState([]); // TODO: multiple add

  const emitDrawPolygon = () => {
    let z = selectedZoneTypeRef.current;
    if (z === "line") {
      z = "polyline";
    }

    eventEmitter.emit(events.draw, { event: "add", type: z });
  };
  const getActiveViewIcon = (shape) => {
    const label = shape?.label;
    const zone = shape?.zone;

    if (invisibleZoneList?.includes(zone)) {
      return false;
    } else {
      if (zone === selectedZone) {
        return visibleAreasList?.includes(label);
      }
      return true;
    }
  };

  const selectedZoneRef = useRef(null);
  React.useEffect(() => {
    selectedZoneRef.current = selectedZone; // because EditControl functions can't access redux state directly (._.')
  }, [selectedZone]);

  const shapePropColorRef = useRef(null);
  React.useEffect(() => {
    shapePropColorRef.current = shapePropColor; // because EditControl functions can't access redux state directly (._.')
  }, [shapePropColor]);

  const allPolyDataRef = useRef(null);

  React.useEffect(() => {
    allPolyDataRef.current = allPolyData; // because EditControl functions can't access redux state directly (._.')
  }, [allPolyData]);

  const drawColorChange = (event) => {
    const { layerType, layer } = event;
    if (layerType === "polyline" || layerType === "polygon") {
      if (
        selectedZoneRef.current &&
        defaultZoneColor[`${selectedZoneRef.current}`]
      ) {
        layer.setStyle({
          color: defaultZoneColor[`${selectedZoneRef.current}`],
        });
      } else if (
        shapePropColorRef.current &&
        shapePropColorRef.current?.includes("#")
      ) {
        layer.setStyle({
          color: shapePropColorRef.current,
          fillColor: "transparent",
        });
      } else {
        layer.setStyle({ color: "aqua", fillColor: "transparent" }); // Set the desired color
      }
    }
  };

  const checkisInsidePerimeter = useCallback(
    (e, type) => {
      let tempAllPolyData = allPolyDataRef.current;
      const allowedPolygon = tempAllPolyData?.find(
        (s) => s.label === "PM1"
      )?.list;
      let isInsideAllowedPolygon = [];
      if (!allowedPolygon) {
        //for driving ranges that do not have perimeters ~ may be removed later
        return true;
      }
      if (type === "create") {
        const drawnShape = e.layer.toGeoJSON();
        if (drawnShape.geometry.type === "Point") {
          // For point, extract the coordinates directly
          const formattedDrawnPoint = [
            drawnShape.geometry.coordinates[1],
            drawnShape.geometry.coordinates[0],
          ];
          // Check if the point's coordinates are within the allowed polygon
          isInsideAllowedPolygon.push(
            booleanPointInPolygon(formattedDrawnPoint, {
              type: "Polygon",
              coordinates: allowedPolygon,
            })
          );
        }
        else if (drawnShape.geometry.type === "LineString") {
          // For polyline, you can iterate over the coordinates
          const polylineCoordinates = drawnShape.geometry.coordinates;

          for (const coord of polylineCoordinates) {
            const formattedDrawnPoint = [coord[1], coord[0]];
            isInsideAllowedPolygon.push(
              booleanPointInPolygon(formattedDrawnPoint, {
                type: "Polygon",
                coordinates: allowedPolygon,
              })
            );
          }
        }
         else if (drawnShape.geometry.type === "Polygon") {
          // For polygon, you can iterate over the exterior ring's coordinates
          const polygonCoordinates = drawnShape.geometry.coordinates[0];
          for (const coord of polygonCoordinates) {
            const formattedDrawnPoint = [coord[1], coord[0]];
            isInsideAllowedPolygon.push(
              booleanPointInPolygon(formattedDrawnPoint, {
                type: "Polygon",
                coordinates: allowedPolygon,
              })
            );
          }
        }
      } 
      else {
        // for edit mode
        let source = Object.values(e?.layers?._layers)[0];
        if (source?.hasOwnProperty("_latlngs")) {
          //   //polyline=>latlngs[]
          //   //polygon=>latlngs[[]]
          let converted = [];
          if (Array.isArray(source?._latlngs[0])) {
            // polygon
            let a = convertPolygonDataToString(source?._latlngs[0].flat());
            converted = preparePointsToLatLong(a)?.flat();
          } else {
            //polyline
            let a = convertPolygonDataToString(source?._latlngs);
            converted = preparePointsToLatLong(a)?.flat();
          }
          for (const coord of converted) {
            isInsideAllowedPolygon.push(
              booleanPointInPolygon(coord, {
                type: "Polygon",
                coordinates: allowedPolygon,
              })
            );
          }
        }
         else if (source?.hasOwnProperty("_latlng")) {
          //marker
          //marker=> latlng {}
          let a = convertPolygonDataToString(source?._latlng);
          let converted = preparePointsToLatLong(a).flat();
          isInsideAllowedPolygon.push(
            booleanPointInPolygon([...converted].flat(), {
              type: "Polygon",
              coordinates: allowedPolygon,
            })
          );
        }
      }
      if (isInsideAllowedPolygon.includes(false)) {
        return false;
      }
      return true;
    },
    [allPolyData]
  );

  const _onCreated = (e) => {
    if (Object.keys(elementGroup)?.includes(selectedGroupRef.current)) {
      let ignoreShape = false;
      if (!checkisInsidePerimeter(e, "create")) {
        ignoreShape = true;
        toastError("Drawn shape must be inside the perimeter.");
        featureGroupRef.current?.removeLayer(e.layer);
      }

      // we don't want to save coordinates for scale
      if (selectedZoneType !== "point") {
        drawColorChange(e);
      }
      if (!ignoreShape) {
        // not saving shapes which are outside perimeter
  
        let data;
        if (e.layerType && singlePointLayers.includes(e.layerType)) {
          data = convertPolygonDataToString(e.layer.getLatLng()); // returns object
        } else if (e.layerType === "polyline") {
          data = convertPolygonDataToString(e.layer.getLatLngs()); //returns array
        } else {
          data = convertPolygonDataToString(e.layer.getLatLngs()[0]); // returns array of array
        }

        setAddedLayer((prevItems) => [...prevItems, e?.layer]);

        //saving new points and drawStatus in redux
        dispatch(setAddedShapePoints(data));
        setAddedPoints((prevItems) => [...prevItems, data]);
      }

      if (
        ignoreShape ||
        !singleShapeElements?.includes(selectedZoneRef.current)
      ) {
        // turn off autodraw enable when element should be one per range if shape is also inside the perimeter
        //else start drawing again
        emitDrawPolygon();
      }
    } else {
      // for polyline drawn by scale button
      let scaleData;
      if (e.layerType === "polyline") {
        scaleData = convertPolygonDataToString(e.layer.getLatLngs()); //returns array
      }
      setScaleLineData(preparePointsToLatLong(scaleData));
      removeSelectedLayer(e?.layer);
    }
  };
  const _onEdited = useCallback(
    (e) => {
      let ignoreShape = false;
      //perimeter can be moved to expand or
      if (
        editableAreaRef.current !== "PM1" &&
        !checkisInsidePerimeter(e, "edit")
      ) {
        toastError("Drawn shape must be inside the perimeter.");
        // featureGroupRef.current?.removeLayer(e.layer);
        ignoreShape = true;
      }
      if (!ignoreShape) {
        let source = Object.values(e?.layers?._layers)[0];
        if (source?.hasOwnProperty("_latlngs")) {
          //   //polyline=>latlngs[]
          //   //polygon=>latlngs[[]]

          if (Array.isArray(source?._latlngs[0])) {
            // polygon
            setEditPolygonData(source?._latlngs[0].flat());
          } else {
            //polyline
            setEditPolygonData(source?._latlngs);
          }
        } else if (source?.hasOwnProperty("_latlng")) {
          //marker=> latlng {}
          setEditMarkerData(source?._latlng);
          //marker
        } else {
          //if only modifers are changed
          let currentElement = selectedZoneDetailsRef.current?.find(
            (s) => s.label === editableAreaRef.current
          );

          let data = currentElement?.points[0];

          if (currentElement?.type === "point") {
            setEditMarkerData(data);
          } else {
            setEditPolygonData(data);
          }
        }
      } else {
        //resetting editable details
        setEditPolygonData(null);
        dispatch(setEditableArea(""));
        dispatch(resetShapeProperties());
      }
      setEditComplete(true);
    },
    [editableArea]
  );

  useEffect(() => {
    if (editComplete === true) {
      dispatch(setAddedShapeStatus("idle"));
      LoadAllPolyData();
    }
    return () => {
      setEditComplete(false);
    };
  }, [editComplete]);

  useEffect(() => {
    if (editPolygonData) {
      const selectedZoneFromEditableArea =
        getSelectedZoneFromEditableArea(editableArea);
      let data =
        typeof editPolygonData === "object"
          ? convertPolygonDataToString(editPolygonData)
          : editPolygonData;

      let shapePayload = convertToAPIPayload(
        data,
        shapeData,
        editableArea,
        // selectedZone,
        selectedZoneFromEditableArea,
        shapePropType,
        shapePropColor
      );
      let reduxData = { shapes: shapePayload };
      dispatch(setMapData(reduxData));
    }

    setEditPolygonData(null);

    dispatch(setEditableArea(""));
    dispatch(resetShapeProperties());
  }, [editPolygonData]);
  useEffect(() => {
    if (editMarkerData) {
      let data =
        typeof editMarkerData === "object"
          ? convertPolygonDataToString(editMarkerData)
          : editMarkerData;
      const selectedZoneFromEditableArea =
        getSelectedZoneFromEditableArea(editableArea);

      let shapePayload = convertToAPIPayload(
        data,
        shapeData,
        editableArea,
        selectedZoneFromEditableArea,
        shapePropType,
        shapePropColor
      );
      let reduxData = { shapes: shapePayload };
      dispatch(setMapData(reduxData)); //API CALL
    }

    setEditMarkerData(null);

    dispatch(setEditableArea(""));
    dispatch(resetShapeProperties());
  }, [editMarkerData]);

  const getMapWorld = (categorizedData) => {
    let flytoZone = convertSingleLatLngStringtoFloatArray(
      categorizedData.facilty?.facilityMarker[0]?.points[0]
    );
    setCurrentLocation(flytoZone);
    // flyTo(flytoZone,ZOOM)

    // setMapView(flytoZone,ZOOM)
  };

  useEffect(() => {
    let categorizedData = catagorizeShapeData(shapeData);

    if (categorizedData) {
      LoadAllPolyData(); // loading all polydata when edits in ShapeData is being done

      getMapWorld(categorizedData);
      let temp = [
        {
          icon: `${asset.star}`,
          isData: true,
          isIcon: true,
          title: elementGroup.Facility,
          data: categorizedData.facilty,
        },
        {
          icon: `${asset.Union}`,
          isData: true,
          isIcon: true,
          title: elementGroup.Areas,
          data: categorizedData.area,
        },
        {
          icon: `${asset.alert}`,
          isData: true,
          isIcon: true,
          title: elementGroup.Hazards,
          data: categorizedData.hazard,
        },
        {
          icon: `${asset.imgTree}`,
          isData: true,
          isIcon: true,
          title: elementGroup.Elements,
          data: categorizedData.element,
        },
        // {
        //   icon: `${asset.battery}`,
        //   isData: false,
        //   isIcon: true,
        //   title: "Areas",
        //   optionListStyleTwo: false,
        // },
      ];
      setMapControlList(temp);
    }
  }, [shapeData, mapRef.current]);

  useEffect(() => {
    if (drawControl) {
      if (!editableArea || editableArea === "") {
        if (selectedZoneType !== "point") {
          drawControl._toolbars.edit._modes.edit.handler.disable();
        }
        eventEmitter.emit(events.addMarkerToggle, { visible: false });
      } else {
        drawControl._toolbars.edit._modes.edit.handler.enable(); // enables all shapes: including scale

        // open popup
        eventEmitter.emit(events.addMarkerToggle, { visible: true });
      }
    }
  }, [editableArea, drawControl, selectedZoneType]);

  const [boundLatLng, setBoundLatLng] = useState(null);
  const LoadAllPolyData = () => {
    const { boundList: boundaryList, elementWiseList } =
      getBoundsAndElementWisePoints(shapeData);
    setBoundLatLng(L.latLngBounds(boundaryList)); //needed for rendering most of the map inside viewport
    addedLayer?.map((layer) => {
      removeSelectedLayer(layer); //removing previously added layer
    });
    setAddedLayer([]);
    setAddedPoints([]);
    setAllPolyData(elementWiseList);
  };

  const cancelEdit = () => {
    if (drawControl) {
      drawControl._toolbars.edit._modes.edit.handler.disable();
      eventEmitter.emit(events.addMarkerToggle, { visible: false });
    }
  };
  const [mapDomEl, setMapDomEl] = useState();
  const onMountedRect = (e) => {
    editControlRef.current = e;
    const div = document.getElementById("GoogleMap");
    setMapDomEl(div);
  };

  const handleZoom = (event) => {
    if (event.deltaY > 0) {
      zoomOut();
    } else {
      zoomIn();
    }
  };
  React.useEffect(() => {
    if (mapDomEl) {
      mapDomEl.addEventListener("wheel", handleZoom);
      return () => {
        mapDomEl.removeEventListener("wheel", handleZoom);
      };
    }
  }, [mapDomEl, isGoogleView]);

  const enableScaleDraw = () => {
    resetDrawingMode(); //disables previously enabled drawing/editing mode
    drawPolyLine(); //starts drawing polyline
  };

  const saveEditedChanges = useCallback(() => {
    let drawControl = editControlRef.current;
    drawControl._toolbars.edit._modes.edit.handler.save();
    drawControl._toolbars.edit._modes.edit.handler.disable();
  }, []);

  const changeMapLayer = () => {
    setIsGoogleView(!isGoogleView);
  };
  const BasicControls = [
    {
      icon: `${asset.plusIcon}`,
      isData: false,
      isIcon: true,
      title: "ZoomIn",
      callBack: zoomIn,
    },
    {
      icon: `${asset.minus}`,
      isData: false,
      isIcon: true,
      title: "ZoomOut",
      callBack: zoomOut,
    },
    {
      icon: isGoogleView ? `${asset.googleIcon}` : `${asset.bingIcon}`,
      isData: false,
      isIcon: true,
      title: "ToggleMap",
      callBack: changeMapLayer,
    },
    {
      icon: `${asset.scale}`,
      isData: false,
      isIcon: true,
      title: "Scale",
      callBack: enableScaleDraw,
      // callBack:drawPolyLineWithMenu
    },
  ];

  const filterDrawActions = useCallback((e) => {
    if (e.type === drawShapeType.polyline && e.event === drawEventType.add) {
      drawPolyLine();
      // setVisibleAddMarker(true)
    } else if (
      e.type === drawShapeType.polygon &&
      e.event === drawEventType.add
    ) {
      drawPolygon(); // listens to drawPolygon
    } else if (
      e.type === drawShapeType.point &&
      e.event === drawEventType.add
    ) {
      drawPoint();
    }
    eventEmitter.emit(events.addMarkerToggle, { visible: true });
  }, []);

  const saveNewShape = (e) => {
    // emittedShape props are used when creating new shape;
    //convertAddedShapeToPayload needs color and type props to create polygon,
    // if  emitted props are undefined, it falls back to default color code and props.
    const emittedShapePropType = e?.markerType;
    const emittedShapePropColor = e?.selectedMarkerMod2;

    let prefix = zonePrefixList[`${selectedZone}`];

    let shapePayload = convertAddedShapeToPayload(
      addedPoints,
      shapeData,
      selectedZone,
      prefix,
      emittedShapePropColor,
      emittedShapePropType
    );
    let reduxData = { shapes: shapePayload };

    dispatch(setMapData(reduxData));
    eventEmitter.emit(events.addMarkerToggle, { visible: false });
    eventEmitter.emit(events.disableDraw);
    setAddedPoints([]); // else  addedPoints have multiple sets of latlongs
    dispatch(setAddedShapeStatus("idle"));
  };
 
  useEffect(() => {
    // mapRef.current?.setZoom(16)  //resetting zoom level when changing map
    if (!isGoogleView && mapRef.current.getZoom() > 18) {
      // resetting zoom level when changing into bing map with more than 18 zoom level;
      //  Bing Layer does not have more than 18 zoom level.
      mapRef.current.setZoom(18);
    }
  }, [isGoogleView]);

  useEffect(() => {
    return () => {
      dispatch(resetShapeProperties());
      dispatch(removeAllMapData());
      eventEmitter.off(events.addMarkerToggle);
    };
  }, []); //[addedShapePoints]

  const getCustomMarker = (shape, color = "grey") => {
    let label = shape?.label;
    return L.divIcon({
      className: "custom-marker2",
      // iconSize: [30, 30],
      // iconAnchor: [15, 30],
      html: `<div class="dialog_icon2" style="background-color:${color};text-shadow:1px 1px 1px #000, -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000;">
      ${label}
      <span style="background-color:${color}"></span>
      </div>`,
    });
  };

  const isEditEnabled = (label) => {
    return editableArea === label;
  };
 

  const toggleEditMode = (label) => {
    if (addedShapeStatus !== "idle") {
      // user has drawing mode active
      return;
    }

    if (isEditEnabled(label)) {
      // setNoProp(false);
      dispatch(setEditableArea(""));
      cancelEdit();
    } else {
      if (!editableArea) {
        dispatch(setEditableArea(label));
      }
    }
  };
  const getShapeComponent = (shape) => {
    const polygonOptions = {
      shapetype: "map",
      // Add your custom key and value here
    };
    if (shape?.type === "polygon") {
      return (
        <Polygon
          customIdentifier={polygonOptions}
          color={
            isColor(shape?.mod2)
              ? shape?.mod2
              : defaultZoneColor[`${shape?.zone}`]
              ? defaultZoneColor[`${shape?.zone}`]
              : "grey"
          }
          positions={shape.list}
          eventHandlers={{
            click: (clicked) => {
              toggleEditMode(shape?.label);
            },
          }}
          fillColor={
            "transparent"
            // isColor(shape?.mod2)
            //   ? shape?.mod2
            //   : defaultZoneColor[`${shape?.zone}`]
            //   ? defaultZoneColor[`${shape?.zone}`]
            //   : "grey"
          }
          key={shape?.label + shape?.mod2}

          // keyname changes when color changes as color change does not trigger polygon re-render
        >
          {!editableArea && ( //show tooltip only when not editing polygon
            <Tooltip sticky>
              {convertToLabelName(shape?.zone)}: {shape?.label}
            </Tooltip>
          )}
        </Polygon>
      );
    }

    if (shape?.type === "point") {
      let temp = shape.list.flat(3);
      let color = isColor(shape?.mod2)
        ? shape?.mod2
        : defaultZoneColor[`${shape?.zone}`]
        ? defaultZoneColor[`${shape?.zone}`]
        : "grey";

      let iconCustom = getCustomMarker(
        shape,
        color
        // defaultZoneColor[`${shape?.zone}`]
      );
      return (
        <Marker
          customIdentifier={polygonOptions}
          position={temp}
          icon={iconCustom}
          key={shape?.label + shape?.mod2}
          eventHandlers={{
            click: (clicked) => {
              toggleEditMode(shape?.label);
            },
          }}
        />
      );
    }

    if (shape?.type === "line") {
      let temp = shape.list.flat();
      return (
        <Polyline
          customIdentifier={polygonOptions}
          color={
            isColor(shape?.mod2)
              ? shape?.mod2
              : defaultZoneColor[`${shape?.zone}`]
              ? defaultZoneColor[`${shape?.zone}`]
              : "grey"
          }
          positions={temp}
          eventHandlers={{
            click: (clicked) => {
              toggleEditMode(shape?.label);
              
            },
          }}
          key={Math.random()}
          // polyline does not render edit mode properly if key does not change ~ it does shallow comparison;
          //as we don't have a color like the polygons, we make do with this!
        >
          {!editableArea && ( //show tooltip only when not editing polygon
            <Tooltip sticky>
              {convertToLabelName(shape?.zone)}: {shape?.label}
            </Tooltip>
          )}
        </Polyline>
      );
    }
  };
 
  const removeSelectedLayer = useCallback(
    (layer) => {
      featureGroupRef.current?.removeLayer(layer);
    },
    [featureGroupRef]
  );
 
  const resetDrawingMode = useCallback(() => {
    eventEmitter.emit(events.addMarkerToggle, { visible: false });
    eventEmitter.emit(events.disableDraw);
    dispatch(setEditableArea(""));
    setEditComplete(true);
  }, [addedLayer]);

  useEscKeyPress(resetDrawingMode);

  const drawStart = (drawEvent) => {
  };
 
  const cleartempShapeAnddisableDraw = () => {
    disableDraw();
    LoadAllPolyData();
  };
  useEventEmitter(events.savePolygon, saveEditedChanges);
  useEventEmitter(events.addNewShape, saveNewShape);
  useEventEmitter(events.draw, filterDrawActions);
  useEventEmitter(events.disableDraw, cleartempShapeAnddisableDraw);
  useEventEmitter(events.resetDrawingMode, resetDrawingMode);
  // const bounds = L.latLngBounds([[48.86012694973457,2.298427820205689], [48.856011677451754,2.294608354568482]]);
  // const bounds = L.latLngBounds([[22.5770146023555,88.42910528182985], [22.575184901089724,88.42395544052125]]);

  const boundsOptions = {
    padding: [20, 20], // Adjust padding as needed
    maxZoom: 17,
  };

  return (
    <div id="DraggableContainer">
      {currentLocation && boundLatLng && (
        <MapContainer
          bounds={boundLatLng}
          boundsOptions={boundsOptions}
          center={currentLocation}
          scrollWheelZoom={false}
          ref={mapRef}
          maxZoom={MAX_ZOOM}
          minZoom={MIN_ZOOM}
          style={{ height: "100vh" }}
          zoomControl={false}
          id="GoogleMap"
          doubleClickZoom={false}
        >
          <FeatureGroup
            eventHandlers={{
              editable: false, // new feature group that has editable prop to false; for shapes that can not be edited
            }}
          >
            {allPolyData?.length > 0 &&
              allPolyData?.map((shape, index) => {
                return (
                  <>
                    {editableArea && editableArea !== "" && (
                      <>
                        {shape?.label !== editableArea && (
                          <>
                            {getActiveViewIcon(shape) && (
                              <>{getShapeComponent(shape)}</>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </>
                );
              })}

            {scaleLineData?.length > 0 && (
              <Polyline
                customIdentifier={{ shapetype: "scale" }}
                color={"red"}
                dashArray={SCALE_DASH}
                positions={scaleLineData}
                eventHandlers={{
                  dblclick: (clicked) => {
                    setScaleLineData([]);
                  },
                }}
                key={`scale_${Math.random()}`}
              >
                <Tooltip sticky>Double click to remove</Tooltip>
              </Polyline>
            )}
          </FeatureGroup>

          <FeatureGroup ref={featureGroupRef}>
            <EditControl
              ref={editControlRef}
              //  position="topright"
              onMounted={onMountedRect}
              onCreated={_onCreated}
              // onEditedStop={edited}
              onEdited={(e) => {
                _onEdited(e);
              }}
              onEditStart={(e) => {
              }}
              onEditVertex={(e) => {
              }} //check if polygon changed
              onEditMove={(e) => {
              }} // check if marker moved
              // draw={drawingOptions()}
              edit={{
                poly: { allowIntersection: false },
              }}
              draw={{
                polygon: {
                  feet: false,
                  metric: false,
                  allowIntersection: false, //shows error message if vertices intersect; by default true

                  // shapeOptions:{color:"yellow"}

                  // showArea: showUnits,
                  // showLength: showUnits,
                  // showRadius: showUnits,
                  // feet: useImperialUnits,
                  // metric: !useImperialUnits,
                  // nautic: false
                },
                polyline: {
                  feet: false,
                  metric: false,
                  // showLength: showUnits,
                  // feet: useImperialUnits,
                  // metric: !useImperialUnits,
                  // nautic: false
                },
                // marker: {
                //   icon: markerOptions,
                // },
              }}
              onDrawStart={(e) => {
                drawStart(e);
              }}
              //not using built-in buttons; instead using useRef
              // need to add display:none for the editcontrol div
            />
            {allPolyData?.length > 0 &&
              allPolyData?.map((shape, index) => {
                return (
                  <>
                    {editableArea && editableArea !== "" ? (
                      <>
                        {shape?.label === editableArea && (
                          <>
                            {getActiveViewIcon(shape) && (
                              <>{getShapeComponent(shape)}</>
                            )}
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        {" "}
                        {getActiveViewIcon(shape) && (
                          <>{getShapeComponent(shape)}</>
                        )}
                      </>
                    )}
                  </>
                );
              })}
          </FeatureGroup>
          {isGoogleView ? (
            <ReactLeafletGoogleLayer
              type={"hybrid"}
              // type={"satellite"}
              maxZoom={MAX_ZOOM}
              minZoom={MIN_ZOOM}
              apiKey={GoogleMapAPIKey}
            />
          ) : (
             <TileLayer
                url={`https://atlas.microsoft.com/map/tile?api-version=2.1&tilesetId=microsoft.imagery&zoom={z}&x={x}&y={y}&subscription-key=${AzureMapAPIKey}`}
                attribution="© Microsoft Azure Maps"
              />
            // <BingLayer
            //   bingkey={ BingMapAPIKey}
            //   type="Aerial"
            // />
          )}
        </MapContainer>
      )}
      <div className={styles.right_list}>
        <div className={stylesRightList.side_list}>
          <ul>
            {BasicControls.map((data, index) => {
              return (
                <li key={data?.title}>
                  <BasicControlOption
                    index={index}
                    icon={data?.icon}
                    isIcon={data?.isIcon}
                    title={data?.title}
                    // isData={data?.isData}
                    onClickEvent={data?.callBack}
                  />
                </li>
              );
            })}
          </ul>
          <ul>
            {mapControlList?.map((data, index) => {
              return (
                <li key={data?.title}>
                  <OptionList
                    index={index}
                    icon={data?.icon}
                    isIcon={data?.isIcon}
                    title={data?.title}
                    // isData={data?.isData}
                    optionListStyleTwo={data?.optionListStyleTwo}
                    noTable={data?.noTable}
                    data={data?.data}
                    cancelEdit={cancelEdit}
                  ></OptionList>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      <CustomPopover
        zone={
          editableArea
            ? getSelectedZoneFromEditableArea(editableArea)
            : selectedZone
        }
        title={
          editableArea
            ? `Edit ${convertToLabelName(
                getSelectedZoneFromEditableArea(editableArea)
              )}`
            : `Add ${convertToLabelName(selectedZone)}`
        }
      />
    </div>
  );
};
export default React.memo(GoogleLeaflet);
